import { useState, useEffect } from "react";
import { PhoneWrapper } from "../../components/PhoneWrapper";
import { AppLayout } from "../../components/AppLayout";
import { TopBar } from "../../components/TopBar";
import { NavMenu } from "./components/NavMenu";
import { DailyView } from "./views/DailyView";
import { MonthlyView } from "./views/MonthlyView";
import { SettingsView } from "./views/SettingsView";
import { WeekView } from "./components/WeekView";
import { HabitModal } from "./modals/HabitModal";
import { ProgressModal } from "./modals/ProgressModal";
import { ContextModal } from "./modals/ContextModal";
import { CategoryModal } from "./modals/CategoryModal";
import { ResetConfirmModal } from "./modals/ResetConfirmModal";
import { useHabitState } from "./hooks/useHabitState";
import { useHabitActions } from "./hooks/useHabitActions";
import { useContextActions } from "./hooks/useContextActions";
import { useCategoryActions } from "./hooks/useCategoryActions";
import { useDataActions } from "./hooks/useDataActions";
import { useTouchHandlers } from "./utils/touchHandlers";
import { TopViews, Habit, Context } from "./types";

export function HomePage() {
  const [topView, setTopView] = useState<TopViews>("daily");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [collapsedCategories, setCollapsedCategories] = useState<number[]>(
    () => {
      try {
        const stored = localStorage.getItem("collapsedCategories");
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    },
  );

  // Use our custom hooks
  const { state, dispatch } = useHabitState();
  const habitActions = useHabitActions(dispatch);
  const contextActions = useContextActions(dispatch);
  const categoryActions = useCategoryActions(dispatch, state.categories);
  const dataActions = useDataActions(dispatch);
  const touchHandlers = useTouchHandlers(
    habitActions.handleEditHabit,
    contextActions.handleEditContext,
  );

  // Save collapsed categories to localStorage
  useEffect(() => {
    localStorage.setItem(
      "collapsedCategories",
      JSON.stringify(collapsedCategories),
    );
  }, [collapsedCategories]);

  // Handler functions
  const handleDayClick = (date: string) => {
    if (new Date(date) > new Date()) return; // Prevent selecting future days
    setSelectedDay(date);
    setCurrentDate(new Date(date));
    setTopView("daily");
  };

  const handleHabitClick = (habit: Habit) => {
    if (
      habit.progressTracking &&
      !state.days
        .find((day) => day.date === selectedDay)
        ?.habits.includes(habit.id)
    ) {
      habitActions.setProgressHabit(habit);
      habitActions.setProgressValue(0);
      habitActions.setProgressModalOpen(true);
    } else {
      habitActions.handleToggleHabit(selectedDay, habit.id, habit.score);
    }
  };

  const handleContextClick = (context: Context) => {
    contextActions.handleToggleContext(selectedDay, context.id);
  };

  const handleNoteChange = (date: string, note: string) => {
    dispatch({
      type: "UPDATE_DAY_NOTE",
      payload: { date, note },
    });
  };

  const handleCategoryToggle = (categoryId: number) => {
    setCollapsedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId],
    );
  };

  const handleProgressSave = () => {
    if (habitActions.progressHabit) {
      habitActions.handleToggleHabit(
        selectedDay,
        habitActions.progressHabit.id,
        habitActions.progressHabit.score,
        habitActions.progressValue === ""
          ? undefined
          : Number(habitActions.progressValue),
      );
    }
    habitActions.setProgressModalOpen(false);
  };

  return (
    <PhoneWrapper>
      <AppLayout
        top={
          <div>
            <TopBar
              center={
                <NavMenu
                  items={[
                    { id: "daily", title: "Daily" },
                    { id: "report", title: "Report" },
                    { id: "settings", title: "Settings" },
                  ]}
                  onSelect={setTopView}
                  selected={topView}
                />
              }
            />
            {topView === "daily" && (
              <div
                style={{
                  padding: "8px 16px 12px 16px",
                  backgroundColor: "#fff",
                }}
              >
                <WeekView
                  currentDate={currentDate}
                  habits={state.habits}
                  days={state.days}
                  selectedDay={selectedDay}
                  onDayClick={handleDayClick}
                />
              </div>
            )}
          </div>
        }
        body={
          <div
            style={{
              padding: "16px",
              backgroundColor: "#fff",
              color: "#000",
              minHeight: "100vh",
              width: "100%",
            }}
          >
            {topView === "daily" && (
              <DailyView
                state={state}
                selectedDay={selectedDay}
                collapsedCategories={collapsedCategories}
                pressStartTime={touchHandlers.pressStartTime}
                pressScale={touchHandlers.pressScale}
                pressedItemId={touchHandlers.pressedItemId}
                onHabitClick={handleHabitClick}
                onContextClick={handleContextClick}
                onAddHabit={habitActions.handleAddHabit}
                onNoteChange={handleNoteChange}
                onCategoryToggle={handleCategoryToggle}
                onPressStart={touchHandlers.handlePressStart}
                onPressMove={touchHandlers.handlePressMove}
                onPressEnd={touchHandlers.handlePressEnd}
                onPressCancel={touchHandlers.handlePressCancel}
              />
            )}

            {topView === "report" && (
              <MonthlyView
                habits={state.habits}
                days={state.days}
                selectedDay={selectedDay}
                selectedPeriod={state.reportPeriod}
                notesEnabled={state.notesEnabled}
                heatmapEnabled={state.heatmapEnabled}
                onDayClick={handleDayClick}
                onPeriodChange={(period) =>
                  dispatch({ type: "SET_REPORT_PERIOD", payload: period })
                }
              />
            )}

            {topView === "settings" && (
              <SettingsView
                state={state}
                dispatch={dispatch}
                onAddHabit={habitActions.handleAddHabit}
                onEditHabit={habitActions.handleEditHabit}
                onAddContext={contextActions.handleAddContext}
                onEditContext={contextActions.handleEditContext}
                onAddCategory={categoryActions.handleAddCategory}
                onEditCategory={categoryActions.handleEditCategory}
                onToggleGreyOut={(value) =>
                  dispatch({ type: "TOGGLE_GREY_OUT", payload: value })
                }
                onToggleContexts={(value) =>
                  dispatch({ type: "TOGGLE_CONTEXTS", payload: value })
                }
                onToggleNotes={(value) =>
                  dispatch({ type: "TOGGLE_NOTES", payload: value })
                }
                onToggleCategories={(value) =>
                  dispatch({ type: "TOGGLE_CATEGORIES", payload: value })
                }
                onToggleHeatmap={(value) =>
                  dispatch({ type: "TOGGLE_HEATMAP", payload: value })
                }
                onSortOrderChange={habitActions.handleSortOrderChange}
              />
            )}

            {/* Modals */}
            <HabitModal
              isOpen={
                habitActions.addHabitDialogOpen ||
                habitActions.editHabitDialogOpen
              }
              isEdit={habitActions.editHabitDialogOpen}
              habit={habitActions.currentHabit}
              categories={state.categories}
              categoriesEnabled={state.categoriesEnabled}
              onSave={habitActions.handleSaveHabit}
              onCancel={() => {
                habitActions.setAddHabitDialogOpen(false);
                habitActions.setEditHabitDialogOpen(false);
              }}
              onArchive={habitActions.handleArchiveHabit}
              onDelete={habitActions.handleRemoveHabit}
              onHabitChange={habitActions.setCurrentHabit}
            />

            <ProgressModal
              isOpen={habitActions.progressModalOpen}
              habit={habitActions.progressHabit}
              value={habitActions.progressValue}
              onValueChange={habitActions.setProgressValue}
              onSave={handleProgressSave}
              onCancel={() => habitActions.setProgressModalOpen(false)}
            />

            <ContextModal
              isOpen={
                contextActions.addContextDialogOpen ||
                contextActions.editContextDialogOpen
              }
              isEdit={contextActions.editContextDialogOpen}
              context={contextActions.currentContext}
              onSave={contextActions.handleSaveContext}
              onCancel={() => {
                contextActions.setAddContextDialogOpen(false);
                contextActions.setEditContextDialogOpen(false);
              }}
              onArchive={contextActions.handleArchiveContext}
              onDelete={contextActions.handleRemoveContext}
              onContextChange={contextActions.setCurrentContext}
            />

            <CategoryModal
              isOpen={
                categoryActions.addCategoryDialogOpen ||
                categoryActions.editCategoryDialogOpen
              }
              isEdit={categoryActions.editCategoryDialogOpen}
              category={categoryActions.currentCategory}
              onSave={categoryActions.handleSaveCategory}
              onCancel={() => {
                categoryActions.setAddCategoryDialogOpen(false);
                categoryActions.setEditCategoryDialogOpen(false);
              }}
              onDelete={categoryActions.handleRemoveCategory}
              onCategoryChange={categoryActions.setCurrentCategory}
            />

            <ResetConfirmModal
              isOpen={dataActions.resetConfirmOpen}
              onConfirm={dataActions.handleResetData}
              onCancel={dataActions.handleResetCancel}
            />
          </div>
        }
      />
    </PhoneWrapper>
  );
}
